/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useEffect, useCallback, useRef } from "react";
import useToristyInfo from "../../../hooks/useToristyInfo";
import useTranslate from "../../../hooks/useTranslate";
import { Button, PreLoading, SvgIcons } from "@tripian/react";
import ImageGallery from "../../../components/ImageGallery/ImageGallery";
import ToristyEmbed from "./ToristyEmbed/ToristyEmbed";
import Model from "@tripian/model";
import * as gtag from "../../../gtag";

interface IToristyTourInfo {
  tourId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ToristyTourInfo: React.FC<IToristyTourInfo> = ({
  tourId,
  isOpen,
  onClose,
}) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showEmbed, setShowEmbed] = useState(false);

  const { fetchToristyInfo, toristyInfo, loadingToristyInfo, toristyInfoFailed } =
    useToristyInfo();
  const { t } = useTranslate();

  const leftRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      fetchToristyInfo(tourId);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (leftRef.current) {
      setLeftHeight(leftRef.current.offsetHeight);
    }
  }, [toristyInfo, loadingToristyInfo, isGalleryOpen]);

  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const nextGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev => (prev + 1) % (toristyInfo?.service?.images?.length ?? 1)
    );
  }, [toristyInfo?.service?.images?.length]);

  const prevGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev =>
        (prev - 1 + (toristyInfo?.service?.images?.length ?? 1)) %
        (toristyInfo?.service?.images?.length ?? 1)
    );
  }, [toristyInfo?.service?.images?.length]);

  const toristyConfig = window.tconfig.PROVIDERS.tourAndTicket.find(
    x => x.id === Model.PROVIDER_ID.TORISTY
  );
  const apiKey = toristyConfig?.apiKey;

  if (!apiKey || !tourId) return null;

  if (!isOpen) return null;

  const images = (toristyInfo?.service?.images ?? []).map(img => img.orig);

  return (
    <>
      {isGalleryOpen && toristyInfo && (
        <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
          >
            <SvgIcons.X width="32" height="32" />
          </button>
          <ImageGallery
            images={(toristyInfo.service?.images ?? []).map(image => image.large)}
            isOpen={isGalleryOpen}
            currentIndex={galleryIndex}
            onClose={closeGallery}
            onChangeIndex={setGalleryIndex}
            onNext={nextGalleryImage}
            onPrev={prevGalleryImage}
          />
        </div>
      )}

      {showEmbed && toristyInfo && (
        <ToristyEmbed
          key={`embed-${tourId}`}
          tourId={tourId}
          apiKey={apiKey}
          mode="toolwithreviews"
          onClose={() => {
            document.body.style.overflow = "auto";
            setShowEmbed(false);
          }}
        />
      )}

      <div className="fixed inset-0 bg-gray-50 z-[1100] overflow-y-auto">
        {/* Sticky header */}
        <div className="sticky top-[60px] md:top-[90px] z-10 bg-gray-50 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
            <button
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition-colors flex-shrink-0"
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
            </button>
            {toristyInfo && (
              <span className="text-sm font-medium text-gray-700 truncate">
                {toristyInfo.service.name}
              </span>
            )}
          </div>
        </div>

        {loadingToristyInfo ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <PreLoading />
          </div>
        ) : toristyInfo === undefined ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4">
                {toristyInfoFailed
                  ? t("trips.toursAndTickets.error.anErrorOccured")
                  : 'Your platform does not support "Toristy" tours.'}
              </p>
              <Button color="primary" text="Close" onClick={onClose} />
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-6 pt-10 pb-16">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] mt-[60px] gap-10 items-start">

              {/* Left column */}
              <div className="space-y-8 min-w-0" ref={leftRef}>

                {/* Image grid - Airbnb style */}
                <div className="relative rounded-3xl overflow-hidden bg-gray-200" style={{ height: "420px" }}>
                  {images.length >= 3 ? (
                    <div className="grid grid-cols-2 gap-1 h-full">
                      <div
                        className="relative cursor-pointer overflow-hidden"
                        onClick={() => openGallery(0)}
                      >
                        <img
                          src={images[0]}
                          alt={toristyInfo.service.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="grid grid-rows-2 gap-1 h-full">
                        <div
                          className="relative cursor-pointer overflow-hidden"
                          onClick={() => openGallery(1)}
                        >
                          <img
                            src={images[1]}
                            alt=""
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div
                          className="relative cursor-pointer overflow-hidden"
                          onClick={() => openGallery(2)}
                        >
                          <img
                            src={images[2]}
                            alt=""
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                          {images.length > 3 && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                +{images.length - 3} photos
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : images.length > 0 ? (
                    <div
                      className="w-full h-full cursor-pointer"
                      onClick={() => openGallery(0)}
                    >
                      <img
                        src={images[0]}
                        alt={toristyInfo.service.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}
                  {images.length > 0 && (
                    <button
                      onClick={() => openGallery(0)}
                      className="absolute bottom-4 right-4 bg-white text-gray-800 text-xs font-semibold px-4 py-2 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center gap-1.5"
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
                      </svg>
                      Show all {images.length} photos
                    </button>
                  )}
                </div>

                {/* Title */}
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                    {toristyInfo.service.name}
                  </h1>
                </div>

                {/* Quick info chips */}
                <div className="flex flex-wrap gap-2">
                  {toristyInfo.service.serviceType && (
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                      <span className="flex-shrink-0 text-gray-400 flex items-center">
                        <SvgIcons.Tags width="16" height="16" />
                      </span>
                      <span>{toristyInfo.service.serviceType}</span>
                    </div>
                  )}
                  {(toristyInfo.service.location.line1 || toristyInfo.service.location.line2) && (
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                      <span className="flex-shrink-0 text-gray-400 flex items-center">
                        <SvgIcons.MapPin width="16" height="16" />
                      </span>
                      <span>{toristyInfo.service.location.line1 || toristyInfo.service.location.line2}</span>
                    </div>
                  )}
                  {toristyInfo.service.starting_price?.text && (
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                      <span className="flex-shrink-0 text-gray-400 flex items-center">
                        <SvgIcons.Euro width="16" height="16" />
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: toristyInfo.service.starting_price.text }} />
                    </div>
                  )}
                </div>

                <hr className="border-gray-200" />

                {/* Mobile Book Now */}
                <div className="lg:hidden">
                  <Button
                    color="primary"
                    className="w-full text-base py-3"
                    onClick={() => {
                      gtag.googleAnalyticsEvent("tour_book_click", { provider: "toristy", tour_id: tourId });
                      setShowEmbed(true);
                    }}
                    text="BOOK NOW"
                  />
                </div>

                {/* About */}
                <div>
                  <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">About This Tour</h2>
                  <div
                    className="text-gray-600 leading-relaxed text-[15px] [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: toristyInfo.service.description }}
                  />
                </div>

                <hr className="border-gray-200" />

                {/* Mobile: What's included */}
                <div className="lg:hidden space-y-6">
                  {(toristyInfo.service.included || toristyInfo.service.excluded) && (
                    <div>
                      <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">What's Included</h2>
                      <div className="space-y-2">
                        {toristyInfo.service.included?.split("<br />").map((item, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                            <span dangerouslySetInnerHTML={{ __html: item.trim() }} />
                          </div>
                        ))}
                        {toristyInfo.service.excluded?.split("<br />").map((item, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm text-gray-500">
                            <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                            <span dangerouslySetInnerHTML={{ __html: item.trim() }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* Right column */}
              <div className="hidden lg:flex flex-col gap-5 self-start">

                {/* Book Now - sticky */}
                <div className="sticky top-[150px] bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
                  {toristyInfo.service.starting_price?.text && (
                    <div
                      className="text-2xl font-bold text-gray-900 mb-4"
                      dangerouslySetInnerHTML={{ __html: toristyInfo.service.starting_price.text }}
                    />
                  )}
                  <Button
                    color="primary"
                    className="w-full text-lg py-3"
                    onClick={() => {
                      gtag.googleAnalyticsEvent("tour_book_click", { provider: "toristy", tour_id: tourId });
                      setShowEmbed(true);
                    }}
                    text="BOOK NOW"
                  />
                </div>

                {/* What's included */}
                {(toristyInfo.service.included || toristyInfo.service.excluded) && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      What's Included
                    </h3>
                    <div className="space-y-2">
                      {toristyInfo.service.included?.split("<br />").map((item, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                          <span dangerouslySetInnerHTML={{ __html: item.trim() }} />
                        </div>
                      ))}
                      {toristyInfo.service.excluded?.split("<br />").map((item, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm text-gray-500">
                          <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                          <span dangerouslySetInnerHTML={{ __html: item.trim() }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Important info */}
                {toristyInfo.service.attention && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                      Important Information
                    </h3>
                    <div
                      className="text-sm text-gray-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li+li]:mt-1"
                      dangerouslySetInnerHTML={{ __html: toristyInfo.service.attention }}
                    />
                  </div>
                )}

              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ToristyTourInfo;
